<template>
  <div>
    <!-- Debug Panel Trigger Button - Only show if debug mode is explicitly enabled via headers -->
    <button 
      v-if="hasRealDebugHeaders && showDebugButton" 
      @click="isVisible = !isVisible" 
      class="debug-trigger-button"
      title="Toggle Debug Panel (Shift+Alt+D)"
    >
      🛠️
    </button>
    
    <!-- Debug overlay panel -->
    <div class="direct-debug-overlay" v-if="isVisible || forceHeaders">
      <div class="debug-panel">
        <div class="debug-header">
          <h4>GO Debug Headers</h4>
          <button @click="toggleExpanded" class="toggle-button">
            {{ isExpanded ? '▲' : '▼' }}
          </button>
        </div>
        <div v-if="isExpanded" class="debug-content">
          <div class="status-section">
            <h5>Request Status</h5>
            <p><strong>Headers detected:</strong> {{ hasRealHeaders ? 'Yes' : 'No' }}</p>
            <p><strong>Debug Mode:</strong> {{ isDebugMode ? 'Enabled' : 'Disabled' }}</p>
            <p>
              <strong>React Native:</strong> 
              <span :class="reactNativeDetected ? 'detected' : 'not-detected'">{{ reactNativeDetected ? 'Detected ✓' : 'Not Detected ✗' }}</span>
            </p>
            <div v-if="reactNativeDetected" class="rn-details">
              <p><strong>Platform:</strong> {{ reactNativePlatform || 'Not specified' }}</p>
              <p><strong>Version:</strong> {{ reactNativeAppVersion || 'Not specified' }}</p>
              <p><strong>Device ID:</strong> {{ reactNativeDeviceId || 'Not specified' }}</p>
            </div>
          </div>
          
          <div class="headers-section">
            <h5>All Headers:</h5>
            <div v-if="Object.keys(allCombinedHeaders).length === 0" class="no-headers">
              No headers found - Using direct header detection
            </div>
            <div v-if="forceHeaders" class="force-debug-notice">
              Debug mode activated by x-go-debugmode header
            </div>
            <table v-if="Object.keys(allCombinedHeaders).length > 0" class="headers-table">
              <thead>
                <tr>
                  <th>Header</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(value, key) in allCombinedHeaders" :key="key">
                  <td class="header-name">{{ key }}</td>
                  <td class="header-value">{{ typeof value === 'string' ? value : JSON.stringify(value) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="manual-debug-section">
            <h5>Manual Debug Mode</h5>
            <button @click="enableDebugMode" class="debug-button">Enable Debug Mode</button>
            <button @click="disableDebugMode" class="debug-button">Disable Debug Mode</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';

const props = withDefaults(defineProps<{
  headers: Record<string, string>;
  isDebugMode?: boolean;
  isReactNative?: boolean;
  platform?: string | null;
  appVersion?: string | null;
  deviceId?: string | null;
}>(), {
  headers: () => ({}),
  isDebugMode: false,
  isReactNative: false,
  platform: null,
  appVersion: null,
  deviceId: null
});

// Panel visibility state
const isVisible = ref(false);
const isExpanded = ref(true);

// Debug button visibility - controlled based on actual headers
// No longer "always visible" - only visible when debug headers exist
const showDebugButton = ref(true);

// Headers collection - combines props headers with cookie headers
const allCombinedHeaders = ref<Record<string, string>>({});

// Check if any real headers exist (not empty strings or default values)
const hasRealHeaders = computed(() => {
  // Filter out empty headers and check if any remain
  const realHeaders = Object.entries(allCombinedHeaders.value).filter(([key, value]) => {
    // Only count non-empty headers with actual values
    return value && value.trim() !== '' && key.startsWith('x-go-');
  });
  
  return realHeaders.length > 0;
});

// IMPORTANT: Only show debug UI if debug header is explicitly present
const hasRealDebugHeaders = computed(() => {
  // Check specifically for x-go-debugmode header with value 'true'
  return Object.entries(allCombinedHeaders.value).some(([key, value]) => {
    return key.toLowerCase() === 'x-go-debugmode' && value === 'true';
  });
});

// Get headers from go-debug-headers cookie
const getHeadersFromCookie = () => {
  if (typeof document === 'undefined') return {};
  
  const cookies = document.cookie.split(';');
  let headersCookie = '';
  
  // Find the go-debug-headers cookie
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'go-debug-headers') {
      headersCookie = value;
      break;
    }
  }
  
  // If found, parse the headers JSON
  if (headersCookie) {
    try {
      const decodedCookie = decodeURIComponent(headersCookie);
      const parsedHeaders = JSON.parse(decodedCookie);
      console.log('DIRECT: Headers found in go-debug-headers cookie', parsedHeaders);
      return parsedHeaders;
    } catch (e) {
      console.error('Failed to parse go-debug-headers cookie', e);
    }
  }
  
  return {};
};

// Force debug ONLY when x-go-debugmode header is present (not ANY x-go-* header)
const directHeaderCheck = () => {
  // First check if cookie forces debug mode
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'go-debug-mode' && value === 'true') {
        console.log('DIRECT: Debug mode forced by go-debug-mode cookie');
        return true;
      }
    }
  }
  
  // FIXED: Only check for specific x-go-debugmode header (not any x-go-* header)
  // Check for the specific debug header in combined headers
  if (Object.keys(allCombinedHeaders.value).some(key => {
    const lcKey = key.toLowerCase();
    // Only return true for the specific debug mode header
    return lcKey === 'x-go-debugmode' && 
           allCombinedHeaders.value[key] === 'true';
  })) {
    console.log('DIRECT: Debug mode header found in combined headers', allCombinedHeaders.value);
    return true;
  }
  
  // FIXED: Only check for specific x-go-debugmode in localStorage
  if (typeof localStorage !== 'undefined') {
    const debugModeValue = localStorage.getItem('x-go-debugmode');
    if (debugModeValue === 'true') {
      console.log('DIRECT: Debug mode found in localStorage');
      return true;
    }
  }
  
  return false;
};

// Check if headers force debug mode
const forceHeaders = computed(() => directHeaderCheck());

// Store direct headers for future use (EXCEPT x-go-client)
const storeHeadersInLocalStorage = () => {
  if (typeof localStorage === 'undefined') return;
  
    // Store all debug headers EXCEPT x-go-client
  // We never store x-go-client to avoid any false detection via persistence
  Object.keys(allCombinedHeaders.value).forEach(key => {
    const lcKey = key.toLowerCase();
    if (lcKey.startsWith('x-go-') && lcKey !== 'x-go-client') {
      localStorage.setItem(lcKey, allCombinedHeaders.value[key]);
      console.log(`Storing debug header in localStorage: ${lcKey}=${allCombinedHeaders.value[key]}`);
    }
  });
  
  // CRITICAL: Always ensure x-go-client is removed from localStorage
  localStorage.removeItem('x-go-client');
  console.log('Ensured x-go-client is not in localStorage');
  
  // Force debug mode when x-go-debugmode is true
  if (allCombinedHeaders.value['x-go-debugmode'] === 'true') {
    localStorage.setItem('force-debug-mode', 'true');
    console.log('Forcing debug mode via localStorage');
  }
};

// Toggle expanded state
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

// Debug mode controls
const enableDebugMode = () => {
  localStorage.setItem('force-debug-mode', 'true');
  window.location.reload();
};

const disableDebugMode = () => {
  localStorage.removeItem('force-debug-mode');
  window.location.reload();
};

// Get direct access to the reactNative global state
const { $reactNative } = useNuxtApp();

// React Native detection computed properties that use the global plugin state
const reactNativeDetected = computed(() => {
  // This will now always be accurate and synchronized
  return $reactNative.isApp;
});

// All these properties now come directly from the reactNative state
const reactNativePlatform = computed(() => {
  return $reactNative.platform;
});

const reactNativeAppVersion = computed(() => {
  return $reactNative.appVersion;
});

const reactNativeDeviceId = computed(() => {
  return $reactNative.deviceId;
});

// For development - add this console log to verify we're looking at the right data
console.log('DirectDebugOverlay: $reactNative.isApp =', $reactNative.isApp);

// For backward compatibility, maintain the extraction logic
const extractReactNativeInfo = (headers: Record<string, string>) => {
  const info = {
    isReactNative: false,
    platform: null as string | null,
    appVersion: null as string | null,
    deviceId: null as string | null,
  };
  
  // Check if we have React Native headers
  if (headers['x-go-client']?.toLowerCase() === 'reactnative') {
    info.isReactNative = true;
    info.platform = headers['x-go-device-platform'] || null;
    info.appVersion = headers['x-go-app-version'] || null;
    info.deviceId = headers['x-go-device-id'] || null;
  }
  
  return info;
};

// For backward compatibility
const extractedReactNativeInfo = ref({
  isReactNative: false,
  platform: null as string | null,
  appVersion: null as string | null,
  deviceId: null as string | null,
});

// Show overlay based on debug mode or keyboard shortcut (Shift+Alt+D)
onMounted(() => {
  if (typeof window !== 'undefined') {
    // CRITICAL: Clear any debug-forcing items in localStorage
    // This ensures debug mode won't persist unless explicitly enabled
    if (typeof localStorage !== 'undefined') {
      // IMPORTANT: Remove force-debug-mode to ensure it's not persisting
      localStorage.removeItem('x-go-client');
      localStorage.removeItem('force-debug-mode');
      console.log('DirectDebugOverlay: Cleared debug-related items from localStorage');
    }
    
    // Combine headers from props and cookies
    const cookieHeaders = getHeadersFromCookie();
    allCombinedHeaders.value = { ...props.headers, ...cookieHeaders };
    
    // Extract React Native information from the combined headers
    extractedReactNativeInfo.value = extractReactNativeInfo(allCombinedHeaders.value);
    console.log('DirectDebugOverlay: Extracted RN Info', extractedReactNativeInfo.value);
    
    // Store headers for future use (will handle x-go-client carefully)
    storeHeadersInLocalStorage();
    
    // Don't check localStorage for forced debug mode on mount
    // This will prevent the debug panel from appearing due to stale localStorage values
    // Only the explicit header check should determine if debug mode is enabled
    
    // Only show panel if explicit debug header exists
    // This ensures the panel is completely hidden unless debug is explicitly enabled
    isVisible.value = hasRealDebugHeaders.value;
    
    // Setup keyboard shortcut (Shift+Alt+D) - ONLY when debug headers exist
    // This prevents the shortcut from working unless debug mode is explicitly enabled
    if (hasRealDebugHeaders.value) {
      window.addEventListener('keydown', (e) => {
        if (e.shiftKey && e.altKey && e.key === 'd') {
          isVisible.value = !isVisible.value;
        }
      });
    }
    
    console.log('DirectDebugOverlay: Headers from props', props.headers);
    console.log('DirectDebugOverlay: Headers from cookie', cookieHeaders);
    console.log('DirectDebugOverlay: Force Headers', forceHeaders.value);
    console.log('DirectDebugOverlay: isVisible', isVisible.value);
    console.log('DirectDebugOverlay: isDebugMode from props', props.isDebugMode);
  }
});
</script>

<style scoped>
.direct-debug-overlay {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  background-color: rgba(255, 255, 255, 0.97);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  max-height: 80vh;
  overflow: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  color: #333;
}

.debug-panel {
  padding: 15px;
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.debug-header h4 {
  margin: 0;
  font-size: 16px;
  color: #9900f1; /* OnePass purple */
}

.toggle-button {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 16px;
}

.status-section,
.headers-section,
.manual-debug-section {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.status-section h5,
.headers-section h5,
.manual-debug-section h5 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
}

.rn-details {
  margin-left: 10px;
  padding-left: 10px;
  border-left: 2px solid #eee;
  margin-top: 5px;
}

.headers-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.headers-table th {
  text-align: left;
  padding: 5px;
  font-weight: bold;
  background: #f7f7f7;
  border-bottom: 1px solid #ddd;
}

.headers-table td {
  padding: 5px;
  border-bottom: 1px solid #eee;
  word-break: break-all;
}

.header-name {
  font-weight: bold;
  color: #555;
}

.header-value {
  color: #777;
  font-family: monospace;
}

.no-headers {
  color: #999;
  font-style: italic;
  padding: 5px 0;
}

.debug-button {
  background: #9900f1;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  margin-right: 8px;
  cursor: pointer;
  font-size: 12px;
}

.debug-button:hover {
  background: #7700b8;
}

.force-debug-notice {
  background: rgba(153, 0, 241, 0.2);
  border-left: 4px solid #9900f1;
  padding: 8px 12px;
  margin: 8px 0;
  font-weight: bold;
}

.detected {
  color: #00c853;
  font-weight: bold;
}

.not-detected {
  color: #ff3d00;
  font-weight: bold;
}

.debug-trigger-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #9900f1;
  color: white;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 9998;
}

.debug-trigger-button:hover {
  background: #8800d8;
}
</style>
